<template>
  <div class="aboutContainer">
    <div>
      <span v-if="isUs">
        <span>
          The LumiNE US software is intended for the visualization of medical
          images to provide insights in anatomy and pathology in preparation of
          surgical treatment. As such, the software allows for the conversion of
          2D patient imaging into 3D models, and for the visualization of 2D and
          3D patient imaging including Augmented Reality.</span
        >
      </span>
      <span v-else>
        <span
          >LumiNE (tradename “Lumi”) is Software as a Medical Device (SaMD) and
          includes:</span
        >
        <li>
          <span
            >Lumi Prepare intended for the preoperative investigation of medical
            scans and 3D models of anatomical and pathological structures of the
            head, with capabilities for shape editing of the 3D models and
            virtual surgical treatment planning, both on 2D displays and in
            Augmented Reality.
          </span>
        </li>
        <li>
          <span
            >Lumi Segment intended to generate 3D models from medical image
            data.
          </span>
        </li>
        <li>
          <span
            >Lumi Referenced intended to aid in the intra-operative surgical
            steps of placing an External Ventricular Drain (EVD).</span
          >
        </li>
      </span>
      <br />
      <span>Full details provided in the Instructions for Use</span>
      <div class="info">
        <div v-if="isUs" class="info">
          <div class="invert-icon ifu-icon"></div>
          <div>
            <span style="margin-top: 12px">
              <a
                href="/LumiNE_US_InstructionsForUse.pdf"
                rel="noopener noreferrer"
                target="_blank"
                >Instructions for Use (IFU) LumiNE US</a
              ></span
            >
          </div>
        </div>
        <div v-else class="info">
          <div class="invert-icon ifu-icon"></div>
          <div>
            <span>
              <a
                href="/LumiPrepare_LumiSegment_InstructionsForUse.pdf"
                rel="noopener noreferrer"
                target="_blank"
                >Instructions for Use (IFU) Lumi Prepare and Segment</a
              ></span
            >
            <span>
              <a
                href="/LumiReferenced_InstructionsForUse.pdf"
                rel="noopener noreferrer"
                target="_blank"
                >Instructions for Use (IFU) Lumi Referenced</a
              ></span
            >
          </div>
        </div>
      </div>
      <span
        >Detailed step-by-step instructions can be found in the
        <span class="userGuide" @click="openUserGuide()">User Guide</span>
      </span>
    </div>
    <hr class="separator" />
    <div class="info">
      <div class="aboutEntry">
        <img class="icon" src="/assets/icon/Regulatory_Address.svg" />
        <div class="aboutText">
          <span>Manufacturer:</span> <span>Augmedit B.V.</span>
          <span>Galerij 15</span> <span>1411 LH Naarden</span>
          <span>The Netherlands</span> <span>Tel. +31 617 1010 94</span>
          <span>support@augmedit.com</span>
          <span>www.augmedit.com</span>
        </div>
      </div>
      <div class="aboutContainer">
        <div class="aboutEntry">
          <img class="icon" src="/assets/icon/Regulatory_Releasedate.svg" />
          <div class="aboutText">
            <span>Date of manufacture:</span>
            <span>{{ versions.ReleaseDate }}</span>
          </div>
        </div>
        <div class="aboutEntry">
          <div class="iconContainer">
            <img
              class="icon"
              src="/assets/icon/Regulatory_Version_nopadding.svg"
            />
          </div>
          <div class="aboutText">
            <span>Software version number:</span>
            <span>Lumi {{ versions.ProductVersion }}</span>
          </div>
        </div>
        <div class="swiss">
          <div class="aboutEntry">
            <div class="iconContainer">
              <img
                class="icon"
                src="/assets/icon/CH-REP.svg"
                style="width: 80px; margin-top: 20px"
              />
            </div>
            <div class="aboutText">
              <span>For Swiss users:</span>
              <span>Swiss AR Services AG</span><span>Industriestrasse 47</span
              ><span>CH-6300 Zug</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="aboutEntry">
      <div class="aboutText">
        <span>Container version numbers:</span>
        <span v-for="item in relevantVersions()" :key="item.SystemID">
          {{ item.SystemName }}: {{ item.version }}
        </span>
      </div>
    </div>
    <hr class="separator" />
    <div v-if="region === 'eu'" class="aboutEntry">
      <div class="iconContainer">
        <img class="udi-icon" src="/assets/icon/Regulatory_MDIcon.svg" />
        <img class="ce-icon" src="/assets/icon/CE_3022-white.svg" />
      </div>
      <div class="aboutText, descriptionText">
        <span
          >Lumi software is a Class IIb medical device and complies with the
          General Safety and Performance Requirements (Annex I) of Regulation
          (EU) 2017/745 on medical devices
        </span>
      </div>
    </div>
    <div class="aboutEntry">
      <div class="iconContainer">
        <img class="invert-icon" src="/assets/icon/udi.png" />
      </div>
      <div v-if="isUs" class="aboutText, descriptionText">
        <span>UDI-DI: LumiNE US 08720892617637</span>
      </div>
      <div v-else class="aboutText, descriptionText">
        <span>Lumi Prepare UDI-DI: 08720892617606 </span>
        <span>Lumi Prepare UDI-PI: 08720892617606{{ udiPostfix }} </span>
        <div class="spacer"></div>
        <span>Lumi Segment UDI-DI: 08720892617613 </span>
        <span>Lumi Segment UDI-PI: 08720892617613{{ udiPostfix }} </span>
        <div class="spacer"></div>
        <span>Lumi Reference UDI-DI: 08720892617620 </span>
        <span>Lumi Reference UDI-PI: 08720892617620{{ udiPostfix }} </span>
      </div>
    </div>
    <hr class="separator" />
    <div v-if="!isUs" class="aboutEntry">
      <img class="invert-icon" src="/assets/icon/iso-warning.png" />
      <span
        >The output data provided by Lumi Prepare and Lumi Segment must be
        subject to careful expert assessment. Segmentations should thereby
        always be verified by a qualified user in Lumi Prepare and Lumi Segment
        by outline analysis on the source medical image, since false positive
        and negative segmentation regions can occur. The user is responsible for
        the adequate evaluation of the results.</span
      >
    </div>
    <div v-if="!isUs" class="aboutEntry">
      <img class="invert-icon" src="/assets/icon/iso-warning.png" />
      <span
        >The output data provided by Lumi Referenced must be subject to careful
        expert assessment. Trajectory planning and registration should thereby
        always be verified by a qualified user in Lumi Referenced. The user is
        responsible for the adequate evaluation of the results.</span
      >
    </div>
    <span
      ><strong
        >For all warnings and more information consult Instructions for
        Use!</strong
      >
    </span>
  </div>
</template>
<script>
import CountryVerification from "@/classes/CountryVerification";
import appSettings from "./appSettings";
import { mapActions, mapState } from "vuex";

export default {
  name: "AppAboutPage",
  props: {
    region: {
      type: String,
      default: Object.default,
    },
  },
  data: () => ({
    versions: {
      ProductName: "",
      ProductVersion: "",
      systems: [
        {
          SystemName: null,
          SystemID: null,
          type: null,
          version: null,
          branch: null,
          build: null,
          showInfo: false,
        },
      ],
    },
  }),
  computed: {
    ...mapState(["idToken"]),
    udiPostfix() {
      if (!this.versions.ReleaseDate) return "";
      const [year, month, day] = this.versions.ReleaseDate.split("-");
      const udiDate = `${year.slice(-2)}${month}${day}`;
      return `(21)${this.versions.ProductVersion}(11)${udiDate}`;
    },
    isUs() {
      return this.region === "us";
    },
  },
  async created() {
    await this.getVersions();
  },
  methods: {
    ...mapActions(["fetchAzure"]),
    async lumiWebGLVersion() {
      const response = await fetch("Modules/buildnr.txt");
      const buildNumber = (await response.text()).trim();
      return {
        SystemName: "Lumi.WebGL",
        type: "app",
        version: buildNumber,
        showInfo: "true",
      };
    },
    openUserGuide() {
      const environment = CountryVerification.getEnvironment();
      const url = `${appSettings.userguideurl}?environment=${
        environment === "localhost" ? "test" : environment
      }&access_token=${this.idToken}`;
      window.open(url).focus();
    },
    async getVersions() {
      this.versions = await this.fetchAzure(
        `${appSettings.remoteurl}/api/version`
      );
      if (CountryVerification.getEnvironment() === "test") {
        this.versions.systems.push(await this.lumiWebGLVersion());
      }
    },
    lumiViewWebVersion() {
      return {
        SystemName: "Lumi.Web",
        type: "app",
        version: appSettings.siteversion,
      };
    },
    relevantVersions() {
      let versions = this.versions.systems.filter((version) => {
        return version.showInfo === "true";
      });
      versions.push(this.lumiViewWebVersion());
      return versions.sort((a, b) => {
        return a.SystemName > b.SystemName ? 1 : -1;
      });
    },
  },
};
</script>
<style scoped>
.userGuide {
  text-decoration: underline;
  display: inline;
}
.userGuide:hover {
  cursor: pointer;
}
.spacer {
  height: 8px;
}
.iconContainer {
  height: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.iconContainer img {
  padding-top: 0.3em;
  display: block;
  margin: 0;
}
.separator {
  border: none;
  border-top: 1px solid white;
  margin: 1px 0;
}
a {
  text-decoration: underline;
}
.ifu-icon {
  width: 35px;
  height: 25px;
  background-size: 35px 25px;
  background-image: url("/public/assets/icon/ifu.png");
  background-repeat: no-repeat;
  display: block;
  margin: 10px;
  margin-right: 30px;
}
.ce-icon {
  width: 70px;
  height: 70px;
}
.invert-icon {
  filter: invert(1) brightness(1.2);
}

.info {
  display: flex;
  flex-direction: row;
}
span {
  display: block;
  text-align: left;
  font-size: 13px;
  line-height: 20px;
}
li {
  margin-left: 20px;
}
.aboutText {
  margin: 0;
}
.aboutContainer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: left;
}
.aboutEntry {
  display: flex;
  width: 100%;
  align-items: center;
  flex-direction: row;
  gap: 1em;
}
.swiss {
  display: flex;
  flex-direction: column;
}
</style>
